import type { PostClientSignup } from '@lt/api/dist/methods/client/types';
import type {
  HandleGetNewCode,
  SetFormType,
  SetRequestStatus,
  SetSharedState,
} from 'src/components/AuthNew/types';
import { AuthFormType, RequestStatus } from 'src/components/AuthNew/types';
import { api } from 'src/api';
import { mergeFullClientDataWithSharedState } from 'src/components/AuthNew/utils';
import type { CLIENT } from '@lt/api/dist/types/client';
import axios from 'axios';
import { useRecaptchaController } from 'src/components/ReCaptchaModal';
import { pushAuthPageView } from '../analytics';

type sharedClient = CLIENT & {
  advertising_mail_accepted: boolean;
};

type Props = {
  handleGetNewCode: HandleGetNewCode;
  setRequestStatus: SetRequestStatus;
  setSharedState: SetSharedState;
  setFormType: SetFormType;
  onSignUp: (client: sharedClient) => void;
  isMobile: boolean;
  shouldSkipPhoneConfirmation: boolean;
};

export const useRegistration = ({
  handleGetNewCode,
  setRequestStatus,
  setFormType,
  setSharedState,
  onSignUp,
  isMobile,
  shouldSkipPhoneConfirmation,
}: Props) => {
  // отвечает за управление капчей
  const { isOpenReCaptcha, handleRecaptchaRequired, captchaCallback } =
    useRecaptchaController();
  async function handleRegistration(data: PostClientSignup): Promise<void> {
    try {
      setRequestStatus(RequestStatus.Submitting);
      const response = await api.postClientSignup(data);
      const { success, client } = response;

      // Used for browser cookie authentication
      if (success) {
        await axios.get(client.extra.auth_url);
        pushAuthPageView(AuthFormType.RegistrationSuccess, isMobile);
      } else if (response?.code === 'captcha_required') {
        handleRecaptchaRequired(() => handleRegistration(data));
        return;
      } else throw new Error('registration not success');

      setSharedState(
        mergeFullClientDataWithSharedState({
          ...client,
          phone: data.client.phone,
          email: data.client.email,
        }),
      );
      onSignUp({
        ...client,
        advertising_mail_accepted: data.client.advertising_mail_accepted,
      });

      if (shouldSkipPhoneConfirmation) {
        setFormType(AuthFormType.SuccessLogin);
      } else {
        setFormType(AuthFormType.ConfirmPhoneWithSMSCode);
      }

      setRequestStatus(RequestStatus.Succeeded);
      handleGetNewCode({ event: 'sign_up' });
    } catch (error) {
      setRequestStatus(RequestStatus.Failed);
      // eslint-disable-next-line no-console
      console.error(error);
      throw error;
    }
  }

  return { handleRegistration, isOpenReCaptcha, captchaCallback };
};
